// Mirrors the backend Catalog::ServiceSellingPermission SUBJECT_TYPES.
export type CatalogServiceSubjectType = 'Staff' | 'PermissionGroup';

// Member-action url suffixes relative to the row's `url` (e.g. archive: "/archive").
export type CatalogServiceRowActions = {
  archive: string;
  restore: string;
  move: string;
};

export type CatalogServiceListItem = {
  id: number;
  name: string;
  price_cents: number;
  sort_order: number;
  archived_at: string | null;
  url: string;
  actions: CatalogServiceRowActions;
};

export type CatalogServiceCategoryListItem = {
  id: number;
  name: string;
  sort_order: number;
  parent_id: number | null;
  archived_at: string | null;
  url: string;
  actions: CatalogServiceRowActions;
  services: CatalogServiceListItem[];
};

export type CatalogServiceCategoriesListResponseData = {
  data: CatalogServiceCategoryListItem[];
  records_total: number;
  records_filtered: number;
};

// Best applicable client-card discount resolved by the backend for one service
// (Catalog::DiscountResolver).
export type ClientServiceDiscount = {
  percent: number;
  price_cents: number;
  card: { name: string };
  client_card_id: number;
  client_card_discount_id: number;
};

export type ClientServiceCategoryService = {
  id: number;
  name: string;
  sort_order: number;
  price_cents: number;
  discount: ClientServiceDiscount | null;
};

// The client-priced service tree (GET catalog/client_service_categories) —
// only active services, no archived_at / url / actions on the rows.
export type ClientServiceCategoryListItem = {
  id: number;
  name: string;
  sort_order: number;
  parent_id: number | null;
  services: ClientServiceCategoryService[];
};

export type ClientServiceCategoriesListResponseData = {
  data: ClientServiceCategoryListItem[];
  records_total: number;
  records_filtered: number;
};

export type CatalogServiceSubjectRef = {
  id: number;
  subject_type: CatalogServiceSubjectType;
  subject_id: number;
};

export type CatalogServicePriceChangeLog = {
  id: number;
  old_price_cents: number;
  new_price_cents: number;
  reason: string | null;
  changed_by: string | null;
  created_at: string;
};

export const BOOKING_TYPES = ['none', 'personal', 'group', 'event'] as const;

export type BookingType = (typeof BOOKING_TYPES)[number];

export const SERVICE_CUSTOM_FIELD_TYPES = [
  'text',
  'number',
  'dropdown',
] as const;

export type ServiceCustomFieldType =
  (typeof SERVICE_CUSTOM_FIELD_TYPES)[number];

export type CatalogServiceCustomField = {
  id: number;
  label: string;
  field_type: ServiceCustomFieldType;
  option_values: string[] | null;
  required: boolean;
  sort_order: number;
};

// Prices are integer major units — the backend stores no fractional currency.
export type CatalogServiceDetail = {
  id: number;
  name: string;
  description: string | null;
  receipt_name: string | null;
  service_category_id: number;
  business_direction_id: number;
  price_cents: number;
  cost_price_cents: number | null;
  specialist_payout_tmp_cents: number | null;
  duration_minutes: number | null;
  requires_specialist: boolean;
  booking_type: BookingType;
  color: string;
  notes: string | null;
  photo_url: string | null;
  sort_order: number;
  archived_at: string | null;
  location_ids: number[];
  specialist_ids: number[];
  service_selling_permissions: CatalogServiceSubjectRef[];
  service_custom_fields: CatalogServiceCustomField[];
  price_change_logs: CatalogServicePriceChangeLog[];
};

export type CatalogServiceShowResponseData = {
  service: CatalogServiceDetail;
};
